// client/src/pages/Admin/ResourceEditor/ResourceEditor.tsx
/**
 * RESOURCE EDITOR COMPONENT
 * 
 * A comprehensive form for creating and editing resources.
 * Supports file uploads, author management, and metadata.
 * 
 * @module ResourceEditor
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
    Resource,
    ResourceCategory,
    ResourceCategoryLabels,
    ResourceStatus,
    CreateResourceDTO,
    ResourceMetadata,
    ResourceMedia,
    ResourceAuthor
} from '../../../shared/models/Resource';
import { FileUpload } from '../../../components/FileUpload';
import { useToast } from '../ResourceManager/hooks/useToast';
import {
    RESOURCE_API,
    RESOURCE_FORM_DEFAULTS,
    RESOURCE_STATUS_OPTIONS,
    FILE_UPLOAD_CONFIG,
    RESOURCE_UPLOAD_MAX_SIZE_MB,
    VALIDATION_RULES,
    TOAST_MESSAGES,
    RESOURCE_EDITOR_MODES,
    ResourceEditorMode,
    RESOURCE_EDITOR_FORCE_LEGACY_KEY,
    resolveResourceEditorModeWithOverride,
    isRichEditorReadOnlyMode,
    isRichEditorChangeEnabled,
    isRichEditorDisabled
} from './constants/resourceEditor.constants';
import { ResourceRichTextEditor } from './richText/ResourceRichTextEditor';
import {
    fromResourceToEditorState,
    fromEditorStateToLegacyHtml
} from './richText/resourceDocumentAdapter';
import {
    validateCategory,
    validateDescription,
    validateExternalUrl,
    validateLanguage,
    validatePublishedDate,
    validateResourceForm,
    validateTitle
} from '../ResourceManager/utils/resourceValidation';
import '../ResourceEditor.css';

// ============================================
// TYPES
// ============================================

interface UploadedFile {
    url: string;
    filename: string;
    size: number;
    mimeType: string;
    downloadCount: number;
}

interface ResourceEditorProps {
    resource?: Resource;
    onSave: () => void;
    onCancel: () => void;
}

interface ResourceEditorForm {
    title: string;
    category: ResourceCategory;
    status: ResourceStatus;
    description: string;
    abstract: string;
    content: string;
    keyFindings: string[];
    authors: ResourceAuthor[];
    publishedDate: string;
    language: string;
    tags: string[];
    isFeatured: boolean;
    isPublic: boolean;
    mainFile?: UploadedFile | null;
    additionalFiles: UploadedFile[];
    media: ResourceMedia[];
    metadata: ResourceMetadata;
    relatedResources: string[];
    projectReferences: string[];
    externalUrl: string;
    doi: string;
    isbn: string;
    recommendedCitation: string;
    seoTitle: string;
    seoDescription: string;
    seoKeywords: string[];
}

type UploadSlot =
    | 'mainFile'
    | 'additionalFiles'
    | 'media';

type FieldErrors = Record<string, string>;

// ============================================
// CONSTANTS
// ============================================

const OPTIONAL_TEXT_FIELDS = [
    'abstract',
    'externalUrl',
    'doi',
    'isbn',
    'recommendedCitation',
    'seoTitle',
    'seoDescription'
] as const;

type OptionalTextField =
    (typeof OPTIONAL_TEXT_FIELDS)[number];

type ResourceUpdatePayload = {
    id: string;
    content?: string;
    mainFile?:
        | CreateResourceDTO['mainFile']
        | null;
} & Partial<
    Omit<
        CreateResourceDTO,
        'content' | 'mainFile'
    >
>;

const ACCEPTED_FILE_EXTENSIONS = new Set(
    FILE_UPLOAD_CONFIG.ACCEPTED_TYPES.map((type) =>
        type.replace('.', '').toLowerCase()
    )
);

const parseDelimitedList = (value: string): string[] => {
    return value
        .split(/\n|,/)
        .map((item) => item.trim())
        .filter(Boolean);
};

const stringifyDelimitedList = (value?: string[]): string => {
    if (!Array.isArray(value) || value.length === 0) {
        return '';
    }

    return value.join('\n');
};

const toDateInputValue = (
    value?: Date | string
): string => {
    if (!value) {
        return '';
    }

    const parsed = new Date(value);

    if (Number.isNaN(parsed.getTime())) {
        return '';
    }

    return parsed.toISOString().split('T')[0];
};

const normalizeOptionalText = (
    value: string
): string | undefined => {
    const trimmed = value.trim();
    return trimmed ? trimmed : undefined;
};

const normalizeAuthors = (
    authors: ResourceEditorForm['authors']
) => {
    return authors
        .map((author) => ({
            name: author.name.trim(),
            ...(author.title && author.title.trim()
                ? {
                      title: author.title.trim()
                  }
                : {}),
            ...(author.affiliation &&
            author.affiliation.trim()
                ? {
                      affiliation:
                          author.affiliation.trim()
                  }
                : {}),
            ...(author.email && author.email.trim()
                ? {
                      email: author.email.trim()
                  }
                : {}),
            ...(typeof author.isLeadAuthor === 'boolean'
                ? {
                      isLeadAuthor:
                          author.isLeadAuthor
                  }
                : {})
        }))
        .filter((author) => author.name);
};

const normalizeMetadata = (
    metadata: ResourceMetadata
) => {
    const normalized: ResourceMetadata = {};

    if (
        typeof metadata.pages === 'number' &&
        Number.isFinite(metadata.pages) &&
        metadata.pages >= 0
    ) {
        normalized.pages = metadata.pages;
    }

    if (
        typeof metadata.language === 'string' &&
        metadata.language.trim()
    ) {
        normalized.language =
            metadata.language.trim();
    }

    if (
        typeof metadata.country === 'string' &&
        metadata.country.trim()
    ) {
        normalized.country =
            metadata.country.trim();
    }

    if (
        typeof metadata.year === 'number' &&
        Number.isInteger(metadata.year) &&
        metadata.year >= 0
    ) {
        normalized.year = metadata.year;
    }

    if (
        typeof metadata.publisher === 'string' &&
        metadata.publisher.trim()
    ) {
        normalized.publisher =
            metadata.publisher.trim();
    }

    return normalized;
};

const normalizeMedia = (media: ResourceMedia[]) => {
    return media
        .map((item) => ({
            url: item.url.trim(),
            ...(item.thumbnail &&
            item.thumbnail.trim()
                ? {
                      thumbnail:
                          item.thumbnail.trim()
                  }
                : {}),
            ...(typeof item.duration === 'number' &&
            Number.isFinite(item.duration) &&
            item.duration >= 0
                ? {
                      duration: item.duration
                  }
                : {}),
            ...(item.caption && item.caption.trim()
                ? {
                      caption: item.caption.trim()
                  }
                : {}),
            ...(item.platform &&
            item.platform.trim()
                ? {
                      platform:
                          item.platform.trim()
                  }
                : {})
        }))
        .filter((item) => item.url);
};

const normalizeResourceFile = (
    file?: UploadedFile | null
) => {
    if (!file || !file.url || !file.filename) {
        return null;
    }

    return {
        url: file.url,
        filename: file.filename,
        size:
            typeof file.size === 'number' &&
            Number.isFinite(file.size) &&
            file.size >= 0
                ? file.size
                : 0,
        mimeType: file.mimeType,
        downloadCount:
            typeof file.downloadCount ===
                'number' &&
            Number.isFinite(file.downloadCount) &&
            file.downloadCount >= 0
                ? file.downloadCount
                : 0
    };
};

const createInitialForm = (
    resource?: Resource
): ResourceEditorForm => {
    return {
        title: resource?.title ?? '',
        category:
            resource?.category ??
            (RESOURCE_FORM_DEFAULTS.category as ResourceCategory),
        status:
            resource?.status ??
            (RESOURCE_FORM_DEFAULTS.status as ResourceStatus),
        description: resource?.description ?? '',
        abstract: resource?.abstract ?? '',
        content: resource?.content ?? '',
        keyFindings: resource?.keyFindings ?? [],
        authors:
            resource?.authors?.map((author) => ({
                ...author
            })) ??
            RESOURCE_FORM_DEFAULTS.authors.map(
                (author) => ({ ...author })
            ),
        publishedDate:
            toDateInputValue(resource?.publishedDate) ||
            toDateInputValue(new Date()),
        language:
            resource?.language ??
            RESOURCE_FORM_DEFAULTS.language,
        tags: resource?.tags ?? [],
        isFeatured:
            resource?.isFeatured ??
            RESOURCE_FORM_DEFAULTS.isFeatured,
        isPublic:
            resource?.isPublic ??
            RESOURCE_FORM_DEFAULTS.isPublic,
        mainFile: resource?.mainFile
            ? {
                  url: resource.mainFile.url,
                  filename:
                      resource.mainFile.filename,
                  size: resource.mainFile.size,
                  mimeType:
                      resource.mainFile.mimeType,
                  downloadCount:
                      resource.mainFile.downloadCount
              }
            : undefined,
        additionalFiles:
            resource?.additionalFiles?.map((file) => ({
                url: file.url,
                filename: file.filename,
                size: file.size,
                mimeType: file.mimeType,
                downloadCount:
                    file.downloadCount
            })) ?? [],
        media:
            resource?.media?.map((item) => ({
                ...item
            })) ?? [],
        metadata: resource?.metadata ?? {},
        relatedResources:
            resource?.relatedResources ?? [],
        projectReferences:
            resource?.projectReferences ?? [],
        externalUrl: resource?.externalUrl ?? '',
        doi: resource?.doi ?? '',
        isbn: resource?.isbn ?? '',
        recommendedCitation:
            resource?.recommendedCitation ?? '',
        seoTitle: resource?.seoTitle ?? '',
        seoDescription:
            resource?.seoDescription ?? '',
        seoKeywords:
            resource?.seoKeywords ?? []
    };
};

const MEDIA_ACCEPTED_MIME_PREFIXES = [
    'image/',
    'video/',
    'audio/'
] as const;

const isNonNull = <T,>(
    value: T | null | undefined
): value is T => {
    return value !== null && value !== undefined;
};

const applyCreateOptionalTextFields = (
    payload: CreateResourceDTO,
    values: {
        abstract?: string;
        content?: string;
        externalUrl?: string;
        doi?: string;
        isbn?: string;
        recommendedCitation?: string;
        seoTitle?: string;
        seoDescription?: string;
    }
) => {
    if (values.abstract !== undefined) {
        payload.abstract = values.abstract;
    }

    if (values.content !== undefined) {
        payload.content = values.content;
    }

    if (values.externalUrl !== undefined) {
        payload.externalUrl = values.externalUrl;
    }

    if (values.doi !== undefined) {
        payload.doi = values.doi;
    }

    if (values.isbn !== undefined) {
        payload.isbn = values.isbn;
    }

    if (
        values.recommendedCitation !==
        undefined
    ) {
        payload.recommendedCitation =
            values.recommendedCitation;
    }

    if (values.seoTitle !== undefined) {
        payload.seoTitle = values.seoTitle;
    }

    if (
        values.seoDescription !== undefined
    ) {
        payload.seoDescription =
            values.seoDescription;
    }
};

// ============================================
// COMPONENT
// ============================================

export const ResourceEditor: React.FC<ResourceEditorProps> = ({
    resource,
    onSave,
    onCancel
}) => {
    const isEditing = !!resource;
    const { showToast } = useToast();

    const [initialForm] = useState<ResourceEditorForm>(
        () => createInitialForm(resource)
    );

    // ============================================
    // STATE
    // ============================================

    const [form, setForm] =
        useState<ResourceEditorForm>(
            initialForm
        );

    const [dirtyFields, setDirtyFields] = useState<
        Set<string>
    >(new Set());

    const [loading, setLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [fieldErrors, setFieldErrors] =
        useState<FieldErrors>({});
    const [uploadState, setUploadState] = useState<Record<UploadSlot, boolean>>({
        mainFile: false,
        additionalFiles: false,
        media: false
    });

    const hasActiveUploads =
        uploadState.mainFile ||
        uploadState.additionalFiles ||
        uploadState.media;

    const editorMode = useMemo<ResourceEditorMode>(
        () => {
            let forceLegacy = false;

            try {
                forceLegacy =
                    localStorage.getItem(
                        RESOURCE_EDITOR_FORCE_LEGACY_KEY
                    ) === 'true';
            } catch {
                forceLegacy = false;
            }

            return resolveResourceEditorModeWithOverride(
                process.env
                    .REACT_APP_RESOURCE_EDITOR_MODE,
                forceLegacy
            );
        },
        []
    );

    const initialEditorState = useMemo(
        () => fromResourceToEditorState(resource),
        [resource]
    );

    const editorContentValue = useMemo(() => {
        if (dirtyFields.has('content')) {
            return form.content;
        }

        if (form.content) {
            return form.content;
        }

        return initialEditorState.contentHtml;
    }, [
        dirtyFields,
        form.content,
        initialEditorState.contentHtml
    ]);

    // ============================================
    // HELPERS
    // ============================================

    const getToken = useCallback((): string | null => {
        try {
            return localStorage.getItem('adminToken');
        } catch {
            return null;
        }
    }, []);

    const getErrorMessage = async (response: Response, fallback: string): Promise<string> => {
        try {
            const data = await response.json();
            return data.error || fallback;
        } catch {
            return fallback;
        }
    };

    const markDirty = useCallback(
        (field: string) => {
            setDirtyFields((prev) => {
                const next = new Set(prev);
                next.add(field);
                return next;
            });
        },
        []
    );

    const updateField = useCallback(
        <K extends keyof ResourceEditorForm>(
            field: K,
            value: ResourceEditorForm[K]
        ) => {
            setForm((prev) => ({
                ...prev,
                [field]: value
            }));

            setFieldErrors((prev) => {
                if (Object.keys(prev).length === 0) {
                    return prev;
                }

                const next = { ...prev };
                const key = String(field);

                if (key === 'title') {
                    if (
                        validateTitle(
                            String(value),
                            VALIDATION_RULES.title
                        ) === undefined
                    ) {
                        delete next.title;
                    }
                }

                if (
                    key === 'category' &&
                    validateCategory(value) ===
                        undefined
                ) {
                    delete next.category;
                }

                if (key === 'description') {
                    if (
                        validateDescription(
                            String(value),
                            VALIDATION_RULES.description
                        ) === undefined
                    ) {
                        delete next.description;
                    }
                }

                if (key === 'publishedDate') {
                    if (
                        validatePublishedDate(
                            String(value)
                        ) === undefined
                    ) {
                        delete next.publishedDate;
                    }
                }

                if (
                    key === 'language' &&
                    validateLanguage(
                        String(value)
                    ) === undefined
                ) {
                    delete next.language;
                }

                if (key === 'externalUrl') {
                    const urlValue =
                        String(value).trim();

                    if (
                        !urlValue ||
                        validateExternalUrl(
                            urlValue
                        ) === undefined
                    ) {
                        delete next.externalUrl;
                    }
                }

                return next;
            });

            markDirty(String(field));
        },
        [markDirty]
    );

    const handleRichEditorChange = useCallback(
        (nextValue: string) => {
            if (
                !isRichEditorChangeEnabled(editorMode)
            ) {
                return;
            }

            const nextLegacyContent =
                fromEditorStateToLegacyHtml({
                    ...initialEditorState,
                    contentHtml: nextValue
                }, {
                    allowCanonicalFallback: false
                });

            updateField('content', nextLegacyContent);
        },
        [editorMode, initialEditorState, updateField]
    );

    const updateMetadataField = useCallback(
        <K extends keyof ResourceMetadata>(
            field: K,
            value: ResourceMetadata[K]
        ) => {
            setForm((prev) => ({
                ...prev,
                metadata: {
                    ...prev.metadata,
                    [field]: value
                }
            }));
            markDirty('metadata');
        },
        [markDirty]
    );

    const updateUploadState = useCallback(
        (
            slot: UploadSlot,
            isUploading: boolean
        ) => {
            setUploadState((prev) => {
                if (prev[slot] === isUploading) {
                    return prev;
                }

                return {
                    ...prev,
                    [slot]: isUploading
                };
            });
        },
        []
    );

    const normalizeFormForPayload = useCallback(
        (source: ResourceEditorForm) => {
            const normalizedTitle =
                source.title.trim();

            const normalizedDescription =
                source.description.trim();

            const normalizedLanguage =
                source.language.trim();

            return {
                title: normalizedTitle,
                category: source.category,
                status: source.status,
                description:
                    normalizedDescription,
                abstract:
                    normalizeOptionalText(
                        source.abstract
                    ),
                content:
                    normalizeOptionalText(
                        source.content
                    ),
                keyFindings: source.keyFindings
                    .map((item) => item.trim())
                    .filter(Boolean),
                authors:
                    normalizeAuthors(
                        source.authors
                    ),
                mainFile:
                    normalizeResourceFile(
                        source.mainFile
                    ),
                additionalFiles:
                    source.additionalFiles
                        .map((file) =>
                            normalizeResourceFile(
                                file
                            )
                        )
                        .filter(isNonNull),
                media: normalizeMedia(
                    source.media
                ),
                publishedDate:
                    source.publishedDate
                        ? new Date(
                              source.publishedDate
                          )
                        : null,
                language: normalizedLanguage,
                tags: source.tags
                    .map((tag) => tag.trim())
                    .filter(Boolean),
                isFeatured:
                    source.isFeatured,
                isPublic: source.isPublic,
                relatedResources:
                    source.relatedResources
                        .map((item) =>
                            item.trim()
                        )
                        .filter(Boolean),
                projectReferences:
                    source.projectReferences
                        .map((item) =>
                            item.trim()
                        )
                        .filter(Boolean),
                externalUrl:
                    normalizeOptionalText(
                        source.externalUrl
                    ),
                doi: normalizeOptionalText(
                    source.doi
                ),
                isbn: normalizeOptionalText(
                    source.isbn
                ),
                recommendedCitation:
                    normalizeOptionalText(
                        source.recommendedCitation
                    ),
                seoTitle:
                    normalizeOptionalText(
                        source.seoTitle
                    ),
                seoDescription:
                    normalizeOptionalText(
                        source.seoDescription
                    ),
                seoKeywords:
                    source.seoKeywords
                        .map((item) =>
                            item.trim()
                        )
                        .filter(Boolean),
                metadata: normalizeMetadata(
                    source.metadata
                )
            };
        },
        []
    );

    const buildCreatePayload = useCallback(() => {
        const normalized =
            normalizeFormForPayload(form);

        const payload: CreateResourceDTO = {
            title: normalized.title,
            category: normalized.category,
            status: normalized.status,
            description:
                normalized.description,
            authors: normalized.authors,
            publishedDate:
                normalized.publishedDate ||
                new Date(),
            language:
                normalized.language ||
                RESOURCE_FORM_DEFAULTS.language,
            tags: normalized.tags,
            isFeatured:
                normalized.isFeatured,
            isPublic: normalized.isPublic,
            keyFindings:
                normalized.keyFindings,
            additionalFiles:
                normalized.additionalFiles as NonNullable<
                    CreateResourceDTO['additionalFiles']
                >,
            media: normalized.media,
            relatedResources:
                normalized.relatedResources,
            projectReferences:
                normalized.projectReferences,
            seoKeywords:
                normalized.seoKeywords,
            metadata: normalized.metadata
        };

        if (normalized.mainFile) {
            payload.mainFile =
                normalized.mainFile;
        }

        applyCreateOptionalTextFields(payload, {
            abstract: normalized.abstract,
            content: normalized.content,
            externalUrl: normalized.externalUrl,
            doi: normalized.doi,
            isbn: normalized.isbn,
            recommendedCitation:
                normalized.recommendedCitation,
            seoTitle: normalized.seoTitle,
            seoDescription:
                normalized.seoDescription
        });

        return payload;
    }, [form, normalizeFormForPayload]);

    const buildUpdatePayload = useCallback(() => {
        if (!resource?._id) {
            return null;
        }

        const normalizedCurrent =
            normalizeFormForPayload(form);

        const normalizedInitial =
            normalizeFormForPayload(
                initialForm
            );

        const payload: ResourceUpdatePayload = {
            id: resource._id
        };

        const changed = (
            key: keyof typeof normalizedCurrent
        ) => {
            return (
                JSON.stringify(
                    normalizedCurrent[key]
                ) !==
                JSON.stringify(
                    normalizedInitial[key]
                )
            );
        };

        if (
            dirtyFields.has('title') &&
            changed('title')
        ) {
            payload.title =
                normalizedCurrent.title;
        }

        if (
            dirtyFields.has('category') &&
            changed('category')
        ) {
            payload.category =
                normalizedCurrent.category;
        }

        if (
            dirtyFields.has('status') &&
            changed('status')
        ) {
            payload.status =
                normalizedCurrent.status;
        }

        if (
            dirtyFields.has('description') &&
            changed('description')
        ) {
            payload.description =
                normalizedCurrent.description;
        }

        for (const field of OPTIONAL_TEXT_FIELDS) {
            if (!dirtyFields.has(field)) {
                continue;
            }

            if (!changed(field)) {
                continue;
            }

            const clearOrValue =
                normalizedCurrent[field] ?? '';

            if (field === 'abstract') {
                payload.abstract =
                    clearOrValue;
            }

            if (field === 'externalUrl') {
                payload.externalUrl =
                    clearOrValue;
            }

            if (field === 'doi') {
                payload.doi =
                    clearOrValue;
            }

            if (field === 'isbn') {
                payload.isbn =
                    clearOrValue;
            }

            if (
                field ===
                'recommendedCitation'
            ) {
                payload.recommendedCitation =
                    clearOrValue;
            }

            if (field === 'seoTitle') {
                payload.seoTitle =
                    clearOrValue;
            }

            if (
                field === 'seoDescription'
            ) {
                payload.seoDescription =
                    clearOrValue;
            }
        }

        if (
            dirtyFields.has('content') &&
            changed('content')
        ) {
            payload.content =
                normalizedCurrent.content ??
                '';
        }

        if (
            dirtyFields.has('publishedDate') &&
            changed('publishedDate') &&
            normalizedCurrent.publishedDate
        ) {
            payload.publishedDate =
                normalizedCurrent.publishedDate;
        }

        if (
            dirtyFields.has('language') &&
            changed('language')
        ) {
            payload.language =
                normalizedCurrent.language;
        }

        if (
            dirtyFields.has('authors') &&
            changed('authors')
        ) {
            payload.authors =
                normalizedCurrent.authors;
        }

        if (
            dirtyFields.has('mainFile') &&
            changed('mainFile')
        ) {
            payload.mainFile =
                normalizedCurrent.mainFile;
        }

        if (
            dirtyFields.has('metadata') &&
            changed('metadata')
        ) {
            payload.metadata =
                normalizedCurrent.metadata;
        }

        if (
            dirtyFields.has('isFeatured') &&
            changed('isFeatured')
        ) {
            payload.isFeatured =
                normalizedCurrent.isFeatured;
        }

        if (
            dirtyFields.has('isPublic') &&
            changed('isPublic')
        ) {
            payload.isPublic =
                normalizedCurrent.isPublic;
        }

        if (
            dirtyFields.has('keyFindings') &&
            changed('keyFindings')
        ) {
            payload.keyFindings =
                normalizedCurrent.keyFindings;
        }

        if (
            dirtyFields.has('tags') &&
            changed('tags')
        ) {
            payload.tags = normalizedCurrent.tags;
        }

        if (
            dirtyFields.has('seoKeywords') &&
            changed('seoKeywords')
        ) {
            payload.seoKeywords =
                normalizedCurrent.seoKeywords;
        }

        if (
            dirtyFields.has(
                'relatedResources'
            ) &&
            changed('relatedResources')
        ) {
            payload.relatedResources =
                normalizedCurrent.relatedResources;
        }

        if (
            dirtyFields.has(
                'projectReferences'
            ) &&
            changed('projectReferences')
        ) {
            payload.projectReferences =
                normalizedCurrent.projectReferences;
        }

        if (
            dirtyFields.has('additionalFiles') &&
            changed('additionalFiles')
        ) {
            payload.additionalFiles =
                normalizedCurrent.additionalFiles;
        }

        if (
            dirtyFields.has('media') &&
            changed('media')
        ) {
            payload.media =
                normalizedCurrent.media;
        }

        return payload;
    }, [
        dirtyFields,
        form,
        initialForm,
        normalizeFormForPayload,
        resource
    ]);

    const toFileAcceptAttribute = useCallback(
        () => {
            return FILE_UPLOAD_CONFIG.ACCEPTED_TYPES.join(',');
        },
        []
    );

    const isFileAllowedByExtension =
        useCallback(
            (filename: string) => {
                const extension =
                    filename
                        .split('.')
                        .pop()
                        ?.toLowerCase() || '';

                return ACCEPTED_FILE_EXTENSIONS.has(
                    extension
                );
            },
            []
        );

    const validateUploadedFile = useCallback(
        (
            fileInfo: {
                name: string;
                size: number;
                type?: string;
            },
            context: string,
            options?: {
                acceptedMimePrefixes?: readonly string[];
            }
        ) => {
            if (
                fileInfo.size >
                FILE_UPLOAD_CONFIG.MAX_SIZE
            ) {
                showToast(
                    `${context} exceeds ${RESOURCE_UPLOAD_MAX_SIZE_MB}MB limit`,
                    'error'
                );

                return false;
            }

            if (
                options
                    ?.acceptedMimePrefixes &&
                options.acceptedMimePrefixes.some(
                    (prefix) =>
                        (fileInfo.type || '').startsWith(
                            prefix
                        )
                )
            ) {
                return true;
            }

            if (
                !isFileAllowedByExtension(
                    fileInfo.name
                )
            ) {
                showToast(
                    `${context} type is not allowed`,
                    'error'
                );

                return false;
            }

            return true;
        },
        [isFileAllowedByExtension, showToast]
    );

    // ============================================
    // VALIDATION
    // ============================================

    const validateForm = (): boolean => {
        setFieldErrors({});

        const nextErrors = validateResourceForm(
            {
                title: form.title,
                description: form.description,
                category: form.category,
                authors: form.authors,
                publishedDate: form.publishedDate,
                language: form.language,
                externalUrl: form.externalUrl
            },
            VALIDATION_RULES
        );

        const firstErrorMessage =
            Object.values(nextErrors)[0];

        if (!firstErrorMessage) {
            return true;
        }

        setFieldErrors(nextErrors);
        showToast(firstErrorMessage, 'error');
        return false;
    };

    // ============================================
    // HANDLERS
    // ============================================

    const handleFileUpload = useCallback((fileInfo: { url: string; publicId: string; name: string; size: number; type: string }) => {
        if (
            !validateUploadedFile(
                {
                    name: fileInfo.name,
                    size: fileInfo.size,
                    type: fileInfo.type
                },
                'Main file'
            )
        ) {
            return;
        }

        const uploadedFile: UploadedFile = {
            url: fileInfo.url,
            filename: fileInfo.name,
            size: fileInfo.size,
            mimeType: fileInfo.type,
            downloadCount: 0
        };

        updateField('mainFile', uploadedFile);
    }, [updateField, validateUploadedFile]);

    const handleAdditionalFileUpload = useCallback((fileInfo: { url: string; publicId: string; name: string; size: number; type: string }) => {
        if (
            !validateUploadedFile(
                {
                    name: fileInfo.name,
                    size: fileInfo.size,
                    type: fileInfo.type
                },
                'Additional file'
            )
        ) {
            return;
        }

        const uploadedFile: UploadedFile = {
            url: fileInfo.url,
            filename: fileInfo.name,
            size: fileInfo.size,
            mimeType: fileInfo.type,
            downloadCount: 0
        };

        setForm((prev) => ({
            ...prev,
            additionalFiles: [
                ...prev.additionalFiles,
                uploadedFile
            ]
        }));
        markDirty('additionalFiles');
    }, [markDirty, validateUploadedFile]);

    const handleMediaUpload = useCallback((fileInfo: { url: string; publicId: string; name: string; size: number; type: string }) => {
        if (
            !validateUploadedFile(
                {
                    name: fileInfo.name,
                    size: fileInfo.size,
                    type: fileInfo.type
                },
                'Media file',
                {
                    acceptedMimePrefixes:
                        MEDIA_ACCEPTED_MIME_PREFIXES
                }
            )
        ) {
            return;
        }

        const mediaItem: ResourceMedia = {
            url: fileInfo.url,
            ...(fileInfo.type.startsWith('image/')
                ? {
                      thumbnail:
                          fileInfo.url
                  }
                : {})
        };

        setForm((prev) => ({
            ...prev,
            media: [
                ...prev.media,
                mediaItem
            ]
        }));
        markDirty('media');
    }, [markDirty, validateUploadedFile]);

    const removeFile = useCallback(() => {
        updateField('mainFile', null);
    }, [updateField]);

    const removeAdditionalFile = useCallback((index: number) => {
        setForm((prev) => ({
            ...prev,
            additionalFiles:
                prev.additionalFiles.filter(
                    (_, fileIndex) =>
                        fileIndex !== index
                )
        }));
        markDirty('additionalFiles');
    }, [markDirty]);

    const removeMediaItem = useCallback((index: number) => {
        setForm((prev) => ({
            ...prev,
            media: prev.media.filter(
                (_, mediaIndex) =>
                    mediaIndex !== index
            )
        }));
        markDirty('media');
    }, [markDirty]);

    const updateMediaItem = useCallback((index: number, key: keyof ResourceMedia, value: string) => {
        setForm((prev) => {
            const nextMedia = prev.media.map(
                (item, mediaIndex) => {
                    if (mediaIndex !== index) {
                        return item;
                    }

                    if (key === 'duration') {
                        const parsedDuration =
                            value.trim() === ''
                                ? undefined
                                : Number(value);

                        return {
                            ...item,
                            duration:
                                parsedDuration !==
                                    undefined &&
                                Number.isFinite(
                                    parsedDuration
                                )
                                    ? parsedDuration
                                    : undefined
                        };
                    }

                    return {
                        ...item,
                        [key]: value
                    };
                }
            );

            return {
                ...prev,
                media: nextMedia
            };
        });
        markDirty('media');
    }, [markDirty]);

    const handleAuthorChange = useCallback((index: number, field: string, value: string) => {
        setForm(prev => {
            const newAuthors = [...(prev.authors || [])];
            newAuthors[index] = { ...newAuthors[index], [field]: value };
            return { ...prev, authors: newAuthors };
        });

        if (field === 'name') {
            setFieldErrors((prev) => {
                if (Object.keys(prev).length === 0) {
                    return prev;
                }

                const next = { ...prev };

                if (value.trim()) {
                    delete next[`author-name-${index}`];
                    delete next.authors;
                }

                return next;
            });
        }

        markDirty('authors');
    }, [markDirty]);

    const handleAuthorLeadAuthorChange = useCallback((index: number, checked: boolean) => {
        setForm((prev) => {
            const newAuthors = [...(prev.authors || [])];
            newAuthors[index] = {
                ...newAuthors[index],
                isLeadAuthor: checked
            };

            return {
                ...prev,
                authors: newAuthors
            };
        });
        markDirty('authors');
    }, [markDirty]);

    const addAuthor = useCallback(() => {
        setForm(prev => ({
            ...prev,
            authors: [
                ...(prev.authors || []),
                {
                    name: '',
                    title: '',
                    affiliation: '',
                    email: '',
                    isLeadAuthor: false
                }
            ]
        }));
        markDirty('authors');
    }, [markDirty]);

    const removeAuthor = useCallback((index: number) => {
        setForm(prev => {
            const newAuthors = [...(prev.authors || [])];
            if (newAuthors.length <= 1) return prev;
            newAuthors.splice(index, 1);
            return { ...prev, authors: newAuthors };
        });
        markDirty('authors');
    }, [markDirty]);

    const handleTagChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        updateField('tags', parseDelimitedList(e.target.value));
    }, [updateField]);

    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();

        if (hasActiveUploads) {
            showToast(
                'Please wait for uploads to finish before saving',
                'info'
            );

            return;
        }

        // Validate form
        if (!validateForm()) return;

        const token = getToken();
        if (!token) {
            showToast('You must be logged in to save resources', 'error');
            return;
        }

        setIsSubmitting(true);
        setLoading(true);

        try {
            const url = isEditing ? RESOURCE_API.UPDATE : RESOURCE_API.CREATE;
            const method = isEditing ? 'PUT' : 'POST';

            const payload = isEditing
                ? buildUpdatePayload()
                : buildCreatePayload();

            if (!payload) {
                throw new Error(
                    'Failed to build resource payload'
                );
            }

            if (
                isEditing &&
                Object.keys(payload).length === 1
            ) {
                showToast(
                    'No changes to save',
                    'info'
                );

                setLoading(false);
                setIsSubmitting(false);
                return;
            }

            const response = await fetch(url, {
                method,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorMsg = await getErrorMessage(response, 'Failed to save resource');
                throw new Error(errorMsg);
            }

            showToast(
                isEditing
                    ? TOAST_MESSAGES.UPDATE_SUCCESS
                    : TOAST_MESSAGES.CREATE_SUCCESS,
                'success'
            );
            onSave();
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to save resource';
            showToast(errorMessage, 'error');
            console.error('❌ Resource Editor Error:', err);
        } finally {
            setLoading(false);
            setIsSubmitting(false);
        }
    }, [
        buildCreatePayload,
        buildUpdatePayload,
        getToken,
        isEditing,
        hasActiveUploads,
        onSave,
        showToast
    ]);

    // ============================================
    // RENDER HELPERS
    // ============================================

    const getFileIcon = (mimeType?: string): string => {
        if (!mimeType) return '📄';
        if (mimeType.includes('pdf')) return '📕';
        if (mimeType.includes('image')) return '🖼️';
        if (mimeType.includes('video')) return '🎬';
        if (mimeType.includes('audio')) return '🎵';
        if (mimeType.includes('word') || mimeType.includes('document')) return '📘';
        if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return '📊';
        if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return '📙';
        return '📄';
    };

    const formatFileSize = (bytes?: number): string => {
        if (!bytes) return '';
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    // ============================================
    // RENDER
    // ============================================

    return (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="resource-editor-title">
            <div className="modal">
                <h2 id="resource-editor-title">
                    {isEditing ? 'Edit Resource' : 'Create New Resource'}
                </h2>

                <form onSubmit={handleSubmit} noValidate>
                    {/* Basic Information */}
                    <div className="form-section">
                        <h3>Basic Information</h3>

                        <div className="form-group">
                            <label htmlFor="title">Title *</label>
                            <input
                                id="title"
                                type="text"
                                value={form.title}
                                onChange={e =>
                                    updateField(
                                        'title',
                                        e.target.value
                                    )
                                }
                                required
                                placeholder="Enter resource title"
                                disabled={isSubmitting}
                                aria-required="true"
                                aria-invalid={
                                    !!fieldErrors.title
                                }
                                aria-describedby={
                                    fieldErrors.title
                                        ? 'title-error'
                                        : undefined
                                }
                            />
                            {fieldErrors.title && (
                                <span
                                    id="title-error"
                                    className="field-error"
                                >
                                    {fieldErrors.title}
                                </span>
                            )}
                        </div>

                        <div className="form-group">
                            <label htmlFor="category">Category *</label>
                            <select
                                id="category"
                                value={form.category}
                                onChange={e =>
                                    updateField(
                                        'category',
                                        e.target
                                            .value as ResourceCategory
                                    )
                                }
                                required
                                disabled={isSubmitting}
                                aria-required="true"
                                aria-invalid={
                                    !!fieldErrors
                                        .category
                                }
                                aria-describedby={
                                    fieldErrors.category
                                        ? 'category-error'
                                        : undefined
                                }
                            >
                                {Object.entries(ResourceCategoryLabels).map(([value, label]) => (
                                    <option key={value} value={value}>{label}</option>
                                ))}
                            </select>
                            {fieldErrors.category && (
                                <span
                                    id="category-error"
                                    className="field-error"
                                >
                                    {fieldErrors.category}
                                </span>
                            )}
                        </div>

                        <div className="form-group">
                            <label htmlFor="status">Status</label>
                            <select
                                id="status"
                                value={form.status}
                                onChange={e =>
                                    updateField(
                                        'status',
                                        e.target
                                            .value as ResourceStatus
                                    )
                                }
                                disabled={isSubmitting}
                            >
                                {RESOURCE_STATUS_OPTIONS.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="form-section">
                        <h3>Content</h3>

                        <div className="form-group">
                            <label htmlFor="description">Description *</label>
                            <textarea
                                id="description"
                                value={form.description}
                                onChange={e =>
                                    updateField(
                                        'description',
                                        e.target.value
                                    )
                                }
                                required
                                rows={3}
                                placeholder="Brief description of the resource"
                                disabled={isSubmitting}
                                aria-required="true"
                                aria-invalid={
                                    !!fieldErrors
                                        .description
                                }
                                aria-describedby={
                                    fieldErrors.description
                                        ? 'description-error'
                                        : undefined
                                }
                            />
                            {fieldErrors.description && (
                                <span
                                    id="description-error"
                                    className="field-error"
                                >
                                    {
                                        fieldErrors.description
                                    }
                                </span>
                            )}
                        </div>

                        <div className="form-group">
                            <label htmlFor="abstract">Abstract</label>
                            <textarea
                                id="abstract"
                                value={form.abstract || ''}
                                onChange={e =>
                                    updateField(
                                        'abstract',
                                        e.target.value
                                    )
                                }
                                rows={3}
                                placeholder="Abstract or executive summary"
                                disabled={isSubmitting}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="content">
                                Full Content
                            </label>
                            {editorMode ===
                            RESOURCE_EDITOR_MODES.LEGACY ? (
                                <textarea
                                    id="content"
                                    value={form.content}
                                    onChange={e =>
                                        updateField(
                                            'content',
                                            e.target.value
                                        )
                                    }
                                    rows={6}
                                    placeholder="Full content (plain text or preformatted HTML)"
                                    disabled={isSubmitting}
                                />
                            ) : (
                                <ResourceRichTextEditor
                                    mode={editorMode}
                                    value={editorContentValue}
                                    onChange={
                                        handleRichEditorChange
                                    }
                                    disabled={
                                        isRichEditorDisabled(
                                            editorMode,
                                            isSubmitting
                                        )
                                    }
                                    readOnly={
                                        isRichEditorReadOnlyMode(
                                            editorMode
                                        )
                                    }
                                />
                            )}
                        </div>

                        <div className="form-group">
                            <label htmlFor="keyFindings">
                                Key Findings
                            </label>
                            <textarea
                                id="keyFindings"
                                value={stringifyDelimitedList(
                                    form.keyFindings
                                )}
                                onChange={e =>
                                    updateField(
                                        'keyFindings',
                                        parseDelimitedList(
                                            e.target
                                                .value
                                        )
                                    )
                                }
                                rows={4}
                                placeholder="One finding per line"
                                disabled={isSubmitting}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="tags">Tags</label>
                            <input
                                id="tags"
                                type="text"
                                value={form.tags?.join(', ') || ''}
                                onChange={handleTagChange}
                                placeholder="Comma-separated tags (e.g., governance, policy, research)"
                                disabled={isSubmitting}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="relatedResources">
                                Related Resources
                            </label>
                            <textarea
                                id="relatedResources"
                                value={stringifyDelimitedList(
                                    form.relatedResources
                                )}
                                onChange={e =>
                                    updateField(
                                        'relatedResources',
                                        parseDelimitedList(
                                            e.target
                                                .value
                                        )
                                    )
                                }
                                rows={3}
                                placeholder="Opaque relationship values, one per line"
                                disabled={isSubmitting}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="projectReferences">
                                Project References
                            </label>
                            <textarea
                                id="projectReferences"
                                value={stringifyDelimitedList(
                                    form.projectReferences
                                )}
                                onChange={e =>
                                    updateField(
                                        'projectReferences',
                                        parseDelimitedList(
                                            e.target
                                                .value
                                        )
                                    )
                                }
                                rows={3}
                                placeholder="Opaque project reference values, one per line"
                                disabled={isSubmitting}
                            />
                        </div>
                    </div>

                    {/* Authors */}
                    <div className="form-section">
                        <h3>Authors</h3>
                        {fieldErrors.authors && (
                            <span
                                id="authors-error"
                                className="field-error"
                            >
                                {fieldErrors.authors}
                            </span>
                        )}
                        {form.authors?.map((author, index) => (
                            <div key={index} className="author-row">
                                <input
                                    id={`author-name-${index}`}
                                    type="text"
                                    placeholder="Name *"
                                    value={author.name}
                                    onChange={e => handleAuthorChange(index, 'name', e.target.value)}
                                    required
                                    disabled={isSubmitting}
                                    aria-required="true"
                                    aria-label={`Author ${index + 1} name`}
                                    aria-invalid={
                                        !!fieldErrors[
                                            `author-name-${index}`
                                        ]
                                    }
                                    aria-describedby={
                                        fieldErrors[
                                            `author-name-${index}`
                                        ]
                                            ? `author-name-${index}-error`
                                            : fieldErrors.authors
                                              ? 'authors-error'
                                              : undefined
                                    }
                                />
                                <input
                                    id={`author-title-${index}`}
                                    type="text"
                                    placeholder="Title"
                                    value={author.title || ''}
                                    onChange={e => handleAuthorChange(index, 'title', e.target.value)}
                                    disabled={isSubmitting}
                                    aria-label={`Author ${index + 1} title`}
                                />
                                <input
                                    id={`author-affiliation-${index}`}
                                    type="text"
                                    placeholder="Affiliation"
                                    value={author.affiliation || ''}
                                    onChange={e => handleAuthorChange(index, 'affiliation', e.target.value)}
                                    disabled={isSubmitting}
                                    aria-label={`Author ${index + 1} affiliation`}
                                />
                                <input
                                    id={`author-email-${index}`}
                                    type="email"
                                    placeholder="Email"
                                    value={author.email || ''}
                                    onChange={e => handleAuthorChange(index, 'email', e.target.value)}
                                    disabled={isSubmitting}
                                    aria-label={`Author ${index + 1} email`}
                                />
                                <label
                                    htmlFor={`author-lead-${index}`}
                                    className="author-lead-label"
                                    aria-label={`Author ${index + 1} lead author control`}
                                >
                                    <input
                                        id={`author-lead-${index}`}
                                        type="checkbox"
                                        checked={Boolean(
                                            author.isLeadAuthor
                                        )}
                                        onChange={e =>
                                            handleAuthorLeadAuthorChange(
                                                index,
                                                e.target
                                                    .checked
                                            )
                                        }
                                        disabled={isSubmitting}
                                        aria-label={`Author ${index + 1} lead author`}
                                    />
                                    Lead
                                </label>
                                {form.authors!.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => removeAuthor(index)}
                                        disabled={isSubmitting}
                                        aria-label={`Remove author ${index + 1}`}
                                    >
                                        ✕
                                    </button>
                                )}
                                {fieldErrors[
                                    `author-name-${index}`
                                ] && (
                                    <span
                                        id={`author-name-${index}-error`}
                                        className="field-error"
                                    >
                                        {
                                            fieldErrors[
                                                `author-name-${index}`
                                            ]
                                        }
                                    </span>
                                )}
                            </div>
                        ))}
                        <button
                            type="button"
                            onClick={addAuthor}
                            className="btn-add"
                            disabled={isSubmitting}
                        >
                            + Add Author
                        </button>
                    </div>

                    {/* Metadata */}
                    <div className="form-section">
                        <h3>Metadata</h3>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="publishedDate">Published Date</label>
                                <input
                                    id="publishedDate"
                                    type="date"
                                    value={form.publishedDate}
                                    onChange={e =>
                                        updateField(
                                            'publishedDate',
                                            e.target.value
                                        )
                                    }
                                    disabled={isSubmitting}
                                    aria-invalid={
                                        !!fieldErrors
                                            .publishedDate
                                    }
                                    aria-describedby={
                                        fieldErrors.publishedDate
                                            ? 'publishedDate-error'
                                            : undefined
                                    }
                                />
                                {fieldErrors.publishedDate && (
                                    <span
                                        id="publishedDate-error"
                                        className="field-error"
                                    >
                                        {
                                            fieldErrors.publishedDate
                                        }
                                    </span>
                                )}
                            </div>

                            <div className="form-group">
                                <label htmlFor="language">Language</label>
                                <input
                                    id="language"
                                    type="text"
                                    value={form.language}
                                    onChange={e =>
                                        updateField(
                                            'language',
                                            e.target.value
                                        )
                                    }
                                    placeholder="en"
                                    disabled={isSubmitting}
                                    aria-invalid={
                                        !!fieldErrors
                                            .language
                                    }
                                    aria-describedby={
                                        fieldErrors.language
                                            ? 'language-error'
                                            : undefined
                                    }
                                />
                                {fieldErrors.language && (
                                    <span
                                        id="language-error"
                                        className="field-error"
                                    >
                                        {fieldErrors.language}
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="metadataPages">
                                    Pages
                                </label>
                                <input
                                    id="metadataPages"
                                    type="number"
                                    min={0}
                                    value={
                                        form
                                            .metadata
                                            .pages ??
                                        ''
                                    }
                                    onChange={e => {
                                        const value =
                                            e.target
                                                .value;
                                        updateMetadataField(
                                            'pages',
                                            value ===
                                                ''
                                                ? undefined
                                                : Number(
                                                      value
                                                  )
                                        );
                                    }}
                                    disabled={isSubmitting}
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="metadataYear">
                                    Year
                                </label>
                                <input
                                    id="metadataYear"
                                    type="number"
                                    min={0}
                                    value={
                                        form
                                            .metadata
                                            .year ??
                                        ''
                                    }
                                    onChange={e => {
                                        const value =
                                            e.target
                                                .value;
                                        updateMetadataField(
                                            'year',
                                            value ===
                                                ''
                                                ? undefined
                                                : Number(
                                                      value
                                                  )
                                        );
                                    }}
                                    disabled={isSubmitting}
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="metadataLanguage">
                                    Metadata Language
                                </label>
                                <input
                                    id="metadataLanguage"
                                    type="text"
                                    value={
                                        form
                                            .metadata
                                            .language ||
                                        ''
                                    }
                                    onChange={e =>
                                        updateMetadataField(
                                            'language',
                                            e.target
                                                .value
                                        )
                                    }
                                    disabled={isSubmitting}
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="metadataCountry">
                                    Country
                                </label>
                                <input
                                    id="metadataCountry"
                                    type="text"
                                    value={
                                        form
                                            .metadata
                                            .country ||
                                        ''
                                    }
                                    onChange={e =>
                                        updateMetadataField(
                                            'country',
                                            e.target
                                                .value
                                        )
                                    }
                                    disabled={isSubmitting}
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="metadataPublisher">
                                Publisher
                            </label>
                            <input
                                id="metadataPublisher"
                                type="text"
                                value={
                                    form.metadata
                                        .publisher ||
                                    ''
                                }
                                onChange={e =>
                                    updateMetadataField(
                                        'publisher',
                                        e.target.value
                                    )
                                }
                                disabled={isSubmitting}
                            />
                        </div>
                    </div>

                    {/* File Upload */}
                    <div className="form-section">
                        <h3>📎 File Upload</h3>

                        <div className="form-group">
                            <label>Upload File (PDF, Image, Document)</label>

                            <div className="file-upload-area">
                                <FileUpload
                                    accept={toFileAcceptAttribute()}
                                    label="📁 Choose a file"
                                    onUpload={handleFileUpload}
                                    maxSizeBytes={FILE_UPLOAD_CONFIG.MAX_SIZE}
                                    onUploadStateChange={(
                                        isUploading
                                    ) =>
                                        updateUploadState(
                                            'mainFile',
                                            isUploading
                                        )
                                    }
                                    disabled={isSubmitting}
                                />

                                {form.mainFile && (
                                    <div className="uploaded-file-info">
                                        <span className="file-icon">
                                            {getFileIcon(form.mainFile.mimeType)}
                                        </span>
                                        <span className="file-name">{form.mainFile.filename}</span>
                                        <span className="file-size">
                                            ({formatFileSize(form.mainFile.size)})
                                        </span>
                                        <button
                                            type="button"
                                            className="remove-file-btn"
                                            onClick={removeFile}
                                            disabled={isSubmitting}
                                            aria-label="Remove file"
                                        >
                                            ✕ Remove
                                        </button>
                                    </div>
                                )}

                                {form.mainFile?.url && form.mainFile.mimeType?.startsWith('image/') && (
                                    <div className="image-preview-wrapper">
                                        <img
                                            src={form.mainFile.url}
                                            alt={form.mainFile.filename}
                                            className="image-preview"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="form-group">
                            <label>
                                Additional Files
                            </label>

                            <div className="file-upload-area">
                                <FileUpload
                                    accept={toFileAcceptAttribute()}
                                    label="📁 Add another file"
                                    onUpload={handleAdditionalFileUpload}
                                    maxSizeBytes={FILE_UPLOAD_CONFIG.MAX_SIZE}
                                    onUploadStateChange={(
                                        isUploading
                                    ) =>
                                        updateUploadState(
                                            'additionalFiles',
                                            isUploading
                                        )
                                    }
                                    disabled={isSubmitting}
                                />

                                {form.additionalFiles
                                    .length > 0 && (
                                    <div className="list-items">
                                        {form.additionalFiles.map(
                                            (
                                                file,
                                                index
                                            ) => (
                                                <div
                                                    key={`${file.url}-${index}`}
                                                    className="uploaded-file-info"
                                                >
                                                    <span className="file-icon">
                                                        {getFileIcon(
                                                            file.mimeType
                                                        )}
                                                    </span>
                                                    <span className="file-name">
                                                        {
                                                            file.filename
                                                        }
                                                    </span>
                                                    <span className="file-size">
                                                        (
                                                        {formatFileSize(
                                                            file.size
                                                        )}
                                                        )
                                                    </span>
                                                    <button
                                                        type="button"
                                                        className="remove-file-btn"
                                                        onClick={() =>
                                                            removeAdditionalFile(
                                                                index
                                                            )
                                                        }
                                                        disabled={isSubmitting}
                                                        aria-label="Remove additional file"
                                                    >
                                                        ✕ Remove
                                                    </button>
                                                </div>
                                            )
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="form-group">
                            <label>
                                Media
                            </label>

                            <div className="file-upload-area">
                                <FileUpload
                                    accept="image/*,video/*,audio/*"
                                    label="🖼️ Add media"
                                    onUpload={handleMediaUpload}
                                    maxSizeBytes={FILE_UPLOAD_CONFIG.MAX_SIZE}
                                    onUploadStateChange={(
                                        isUploading
                                    ) =>
                                        updateUploadState(
                                            'media',
                                            isUploading
                                        )
                                    }
                                    disabled={isSubmitting}
                                />

                                {form.media.length >
                                    0 && (
                                    <div className="list-items">
                                        {form.media.map(
                                            (
                                                item,
                                                index
                                            ) => (
                                                <div
                                                    key={`${item.url}-${index}`}
                                                    className="media-item"
                                                >
                                                    <div className="uploaded-file-info">
                                                        <span className="file-icon">
                                                            🖼️
                                                        </span>
                                                        <span className="file-name">
                                                            {item.url}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            className="remove-file-btn"
                                                            onClick={() =>
                                                                removeMediaItem(
                                                                    index
                                                                )
                                                            }
                                                            disabled={isSubmitting}
                                                            aria-label="Remove media item"
                                                        >
                                                            ✕ Remove
                                                        </button>
                                                    </div>

                                                    <div className="form-row">
                                                        <div className="form-group">
                                                            <label htmlFor={`media-caption-${index}`}>
                                                                Caption
                                                            </label>
                                                            <input
                                                                id={`media-caption-${index}`}
                                                                type="text"
                                                                value={item.caption || ''}
                                                                onChange={e =>
                                                                    updateMediaItem(
                                                                        index,
                                                                        'caption',
                                                                        e.target.value
                                                                    )
                                                                }
                                                                disabled={isSubmitting}
                                                            />
                                                        </div>
                                                        <div className="form-group">
                                                            <label htmlFor={`media-platform-${index}`}>
                                                                Platform
                                                            </label>
                                                            <input
                                                                id={`media-platform-${index}`}
                                                                type="text"
                                                                value={item.platform || ''}
                                                                onChange={e =>
                                                                    updateMediaItem(
                                                                        index,
                                                                        'platform',
                                                                        e.target.value
                                                                    )
                                                                }
                                                                disabled={isSubmitting}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="form-row">
                                                        <div className="form-group">
                                                            <label htmlFor={`media-thumbnail-${index}`}>
                                                                Thumbnail URL
                                                            </label>
                                                            <input
                                                                id={`media-thumbnail-${index}`}
                                                                type="text"
                                                                value={item.thumbnail || ''}
                                                                onChange={e =>
                                                                    updateMediaItem(
                                                                        index,
                                                                        'thumbnail',
                                                                        e.target.value
                                                                    )
                                                                }
                                                                disabled={isSubmitting}
                                                            />
                                                        </div>
                                                        <div className="form-group">
                                                            <label htmlFor={`media-duration-${index}`}>
                                                                Duration (seconds)
                                                            </label>
                                                            <input
                                                                id={`media-duration-${index}`}
                                                                type="number"
                                                                min={0}
                                                                value={item.duration ?? ''}
                                                                onChange={e =>
                                                                    updateMediaItem(
                                                                        index,
                                                                        'duration',
                                                                        e.target.value
                                                                    )
                                                                }
                                                                disabled={isSubmitting}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* References */}
                    <div className="form-section">
                        <h3>References</h3>

                        <div className="form-group">
                            <label htmlFor="externalUrl">
                                External URL
                            </label>
                            <input
                                id="externalUrl"
                                type="url"
                                value={form.externalUrl}
                                onChange={e =>
                                    updateField(
                                        'externalUrl',
                                        e.target.value
                                    )
                                }
                                placeholder="https://example.org/resource"
                                disabled={isSubmitting}
                                aria-invalid={
                                    !!fieldErrors
                                        .externalUrl
                                }
                                aria-describedby={
                                    fieldErrors.externalUrl
                                        ? 'externalUrl-error'
                                        : undefined
                                }
                            />
                            {fieldErrors.externalUrl && (
                                <span
                                    id="externalUrl-error"
                                    className="field-error"
                                >
                                    {
                                        fieldErrors.externalUrl
                                    }
                                </span>
                            )}
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="doi">
                                    DOI
                                </label>
                                <input
                                    id="doi"
                                    type="text"
                                    value={form.doi}
                                    onChange={e =>
                                        updateField(
                                            'doi',
                                            e.target.value
                                        )
                                    }
                                    disabled={isSubmitting}
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="isbn">
                                    ISBN
                                </label>
                                <input
                                    id="isbn"
                                    type="text"
                                    value={form.isbn}
                                    onChange={e =>
                                        updateField(
                                            'isbn',
                                            e.target.value
                                        )
                                    }
                                    disabled={isSubmitting}
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="recommendedCitation">
                                Recommended Citation
                            </label>
                            <textarea
                                id="recommendedCitation"
                                value={
                                    form.recommendedCitation
                                }
                                onChange={e =>
                                    updateField(
                                        'recommendedCitation',
                                        e.target.value
                                    )
                                }
                                rows={3}
                                disabled={isSubmitting}
                            />
                        </div>
                    </div>

                    {/* SEO */}
                    <div className="form-section">
                        <h3>SEO</h3>

                        <div className="form-group">
                            <label htmlFor="seoTitle">
                                SEO Title
                            </label>
                            <input
                                id="seoTitle"
                                type="text"
                                value={form.seoTitle}
                                onChange={e =>
                                    updateField(
                                        'seoTitle',
                                        e.target.value
                                    )
                                }
                                disabled={isSubmitting}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="seoDescription">
                                SEO Description
                            </label>
                            <textarea
                                id="seoDescription"
                                value={form.seoDescription}
                                onChange={e =>
                                    updateField(
                                        'seoDescription',
                                        e.target.value
                                    )
                                }
                                rows={3}
                                disabled={isSubmitting}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="seoKeywords">
                                SEO Keywords
                            </label>
                            <textarea
                                id="seoKeywords"
                                value={stringifyDelimitedList(
                                    form.seoKeywords
                                )}
                                onChange={e =>
                                    updateField(
                                        'seoKeywords',
                                        parseDelimitedList(
                                            e.target
                                                .value
                                        )
                                    )
                                }
                                rows={3}
                                placeholder="One keyword per line"
                                disabled={isSubmitting}
                            />
                        </div>
                    </div>

                    {/* Visibility */}
                    <div className="form-section">
                        <h3>Visibility</h3>

                        <div className="checkbox-group">
                            <label htmlFor="isFeatured">
                                <input
                                    id="isFeatured"
                                    type="checkbox"
                                    checked={form.isFeatured}
                                    onChange={e =>
                                        updateField(
                                            'isFeatured',
                                            e.target.checked
                                        )
                                    }
                                    disabled={isSubmitting}
                                />
                                Featured Resource
                            </label>
                            <label htmlFor="isPublic">
                                <input
                                    id="isPublic"
                                    type="checkbox"
                                    checked={form.isPublic}
                                    onChange={e =>
                                        updateField(
                                            'isPublic',
                                            e.target.checked
                                        )
                                    }
                                    disabled={isSubmitting}
                                />
                                Public (visible to all)
                            </label>
                        </div>
                    </div>

                    {/* Form Actions */}
                    <div className="form-actions">
                        <button
                            type="submit"
                            className="btn-primary"
                            disabled={
                                isSubmitting ||
                                loading ||
                                hasActiveUploads
                            }
                        >
                            {loading ? 'Saving...' : (isEditing ? 'Update Resource' : 'Create Resource')}
                        </button>
                        <button
                            type="button"
                            onClick={onCancel}
                            className="btn-secondary"
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ResourceEditor;